import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Settings, Building2, DollarSign, Database, Save, AlertCircle, CheckCircle2, Plus, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import companySettingsService from '../../services/companySettingsService';

export default function SystemAdministrationControlCenter() {
  const [activeTab, setActiveTab] = useState('organization');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Organization Data
  const [organizationData, setOrganizationData] = useState({
    companyName: '',
    legalName: '',
    taxId: '',
    businessRegNumber: '',
    address: '',
    phone: '',
    email: ''
  });

  // Branches
  const [branches, setBranches] = useState([]);
  const [newBranch, setNewBranch] = useState({ name: '', location: '', code: '' });

  // Departments
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState({ name: '', code: '', costCenter: '' });

  // Payroll Settings
  const [payrollSettings, setPayrollSettings] = useState({
    payPeriod: 'fortnightly',
    overtimeMultiplier: '1.5',
    sundayMultiplier: '2.0',
    nasfundEmployeeRate: '6',
    nasfundEmployerRate: '8.4',
    minimumWage: '3.50'
  });

  // Bank Configurations
  const [bankConfigs, setBankConfigs] = useState([
    { id: 1, name: 'BSP', code: '001', active: false, apiEndpoint: '', apiKey: '' },
    { id: 2, name: 'ANZ', code: '002', active: false, apiEndpoint: '', apiKey: '' },
    { id: 3, name: 'WPAC', code: '003', active: false, apiEndpoint: '', apiKey: '' },
    { id: 4, name: 'Kinabank', code: '004', active: false, apiEndpoint: '', apiKey: '' },
    { id: 5, name: 'Credit Corp', code: '005', active: false, apiEndpoint: '', apiKey: '' }
  ]);

  // GL Interface
  const [glInterface, setGlInterface] = useState({
    enabled: false,
    erpSystem: '',
    accountMapping: {
      salariesAccount: '',
      taxAccount: '',
      superAccount: '',
      deductionsAccount: ''
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await companySettingsService?.getActiveCompanySettings();
      if (settings) {
        setOrganizationData({
          companyName: settings?.companyName || '',
          legalName: settings?.companyLegalName || '',
          taxId: settings?.taxIdentificationNumber || '',
          businessRegNumber: settings?.businessRegistrationNumber || '',
          address: settings?.companyAddress || '',
          phone: settings?.contactPhone || '',
          email: settings?.contactEmail || ''
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Save based on active tab
      if (activeTab === 'organization') {
        const settings = await companySettingsService?.getActiveCompanySettings();
        if (settings?.id) {
          await companySettingsService?.updateCompanySettings(settings?.id, {
            companyName: organizationData?.companyName,
            companyLegalName: organizationData?.legalName,
            taxIdentificationNumber: organizationData?.taxId,
            businessRegistrationNumber: organizationData?.businessRegNumber,
            companyAddress: organizationData?.address,
            contactPhone: organizationData?.phone,
            contactEmail: organizationData?.email
          });
        }
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addBranch = () => {
    if (newBranch?.name && newBranch?.location && newBranch?.code) {
      setBranches([...branches, { ...newBranch, id: Date?.now() }]);
      setNewBranch({ name: '', location: '', code: '' });
    }
  };

  const removeBranch = (id) => {
    setBranches(branches?.filter(b => b?.id !== id));
  };

  const addDepartment = () => {
    if (newDepartment?.name && newDepartment?.code) {
      setDepartments([...departments, { ...newDepartment, id: Date?.now() }]);
      setNewDepartment({ name: '', code: '', costCenter: '' });
    }
  };

  const removeDepartment = (id) => {
    setDepartments(departments?.filter(d => d?.id !== id));
  };

  const toggleBankActive = (bankId) => {
    setBankConfigs(bankConfigs?.map(bank => 
      bank?.id === bankId ? { ...bank, active: !bank?.active } : bank
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>System Administration Control Center - PNG HR Analytics</title>
        <meta name="description" content="Comprehensive system-wide configuration management for PNG HR Analytics platform administrators" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-foreground">System Administration Control Center</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Comprehensive system-wide configuration management for PNG HR Analytics platform
                  </p>
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Alert Messages */}
          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-destructive/90 mt-1">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-success">Success</h3>
                <p className="text-sm text-success/90 mt-1">Settings saved successfully</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="col-span-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-lg font-semibold text-foreground mb-4">Configuration Sections</h2>
                <nav className="space-y-2">
                  <button
                    onClick={() => setActiveTab('organization')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'organization' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                    }`}
                  >
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Organization Information
                  </button>
                  <button
                    onClick={() => setActiveTab('branches')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'branches' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                    }`}
                  >
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Branches
                  </button>
                  <button
                    onClick={() => setActiveTab('departments')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'departments' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                    }`}
                  >
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Departments
                  </button>
                  <button
                    onClick={() => setActiveTab('payroll')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'payroll' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                    }`}
                  >
                    <DollarSign className="h-4 w-4 inline mr-2" />
                    Payroll Settings
                  </button>
                  <button
                    onClick={() => setActiveTab('banks')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'banks' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                    }`}
                  >
                    <Database className="h-4 w-4 inline mr-2" />
                    Bank Configurations
                  </button>
                  <button
                    onClick={() => setActiveTab('gl')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'gl' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-foreground'
                    }`}
                  >
                    <Database className="h-4 w-4 inline mr-2" />
                    GL Interface Control
                  </button>
                </nav>
              </div>
            </div>

            {/* Main Panel */}
            <div className="col-span-8">
              {/* Organization Information */}
              {activeTab === 'organization' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Organization Information</h2>
                  <div className="space-y-4">
                    <Input
                      label="Company Name"
                      value={organizationData?.companyName}
                      onChange={(e) => setOrganizationData({ ...organizationData, companyName: e?.target?.value })}
                      required
                    />
                    <Input
                      label="Legal Name"
                      value={organizationData?.legalName}
                      onChange={(e) => setOrganizationData({ ...organizationData, legalName: e?.target?.value })}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Tax ID Number"
                        value={organizationData?.taxId}
                        onChange={(e) => setOrganizationData({ ...organizationData, taxId: e?.target?.value })}
                      />
                      <Input
                        label="Business Registration Number"
                        value={organizationData?.businessRegNumber}
                        onChange={(e) => setOrganizationData({ ...organizationData, businessRegNumber: e?.target?.value })}
                      />
                    </div>
                    <Input
                      label="Address"
                      value={organizationData?.address}
                      onChange={(e) => setOrganizationData({ ...organizationData, address: e?.target?.value })}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Phone"
                        value={organizationData?.phone}
                        onChange={(e) => setOrganizationData({ ...organizationData, phone: e?.target?.value })}
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={organizationData?.email}
                        onChange={(e) => setOrganizationData({ ...organizationData, email: e?.target?.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Branches */}
              {activeTab === 'branches' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Branch Management</h2>
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        label="Branch Name"
                        value={newBranch?.name}
                        onChange={(e) => setNewBranch({ ...newBranch, name: e?.target?.value })}
                        placeholder="e.g., Port Moresby"
                      />
                      <Input
                        label="Location"
                        value={newBranch?.location}
                        onChange={(e) => setNewBranch({ ...newBranch, location: e?.target?.value })}
                        placeholder="e.g., NCD"
                      />
                      <Input
                        label="Branch Code"
                        value={newBranch?.code}
                        onChange={(e) => setNewBranch({ ...newBranch, code: e?.target?.value })}
                        placeholder="e.g., POM001"
                      />
                    </div>
                    <Button onClick={addBranch} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Branch
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {branches?.map((branch) => (
                      <div key={branch?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{branch?.name}</p>
                          <p className="text-sm text-muted-foreground">{branch?.location} - {branch?.code}</p>
                        </div>
                        <Button onClick={() => removeBranch(branch?.id)} variant="ghost" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {branches?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No branches added yet</p>
                    )}
                  </div>
                </div>
              )}

              {/* Departments */}
              {activeTab === 'departments' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Department Management</h2>
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-3 gap-4">
                      <Input
                        label="Department Name"
                        value={newDepartment?.name}
                        onChange={(e) => setNewDepartment({ ...newDepartment, name: e?.target?.value })}
                        placeholder="e.g., Human Resources"
                      />
                      <Input
                        label="Department Code"
                        value={newDepartment?.code}
                        onChange={(e) => setNewDepartment({ ...newDepartment, code: e?.target?.value })}
                        placeholder="e.g., HR"
                      />
                      <Input
                        label="Cost Center"
                        value={newDepartment?.costCenter}
                        onChange={(e) => setNewDepartment({ ...newDepartment, costCenter: e?.target?.value })}
                        placeholder="e.g., CC-001"
                      />
                    </div>
                    <Button onClick={addDepartment} size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Department
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {departments?.map((dept) => (
                      <div key={dept?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium text-foreground">{dept?.name}</p>
                          <p className="text-sm text-muted-foreground">{dept?.code} - Cost Center: {dept?.costCenter}</p>
                        </div>
                        <Button onClick={() => removeDepartment(dept?.id)} variant="ghost" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {departments?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No departments added yet</p>
                    )}
                  </div>
                </div>
              )}

              {/* Payroll Settings */}
              {activeTab === 'payroll' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Payroll System Configuration</h2>
                  <div className="space-y-4">
                    <Select
                      label="Pay Period"
                      value={payrollSettings?.payPeriod}
                      onChange={(e) => setPayrollSettings({ ...payrollSettings, payPeriod: e?.target?.value })}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                    </Select>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Overtime Multiplier"
                        type="number"
                        step="0.1"
                        value={payrollSettings?.overtimeMultiplier}
                        onChange={(e) => setPayrollSettings({ ...payrollSettings, overtimeMultiplier: e?.target?.value })}
                      />
                      <Input
                        label="Sunday Multiplier"
                        type="number"
                        step="0.1"
                        value={payrollSettings?.sundayMultiplier}
                        onChange={(e) => setPayrollSettings({ ...payrollSettings, sundayMultiplier: e?.target?.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="NASFUND Employee Rate (%)"
                        type="number"
                        step="0.1"
                        value={payrollSettings?.nasfundEmployeeRate}
                        onChange={(e) => setPayrollSettings({ ...payrollSettings, nasfundEmployeeRate: e?.target?.value })}
                      />
                      <Input
                        label="NASFUND Employer Rate (%)"
                        type="number"
                        step="0.1"
                        value={payrollSettings?.nasfundEmployerRate}
                        onChange={(e) => setPayrollSettings({ ...payrollSettings, nasfundEmployerRate: e?.target?.value })}
                      />
                    </div>
                    <Input
                      label="Minimum Wage (PGK per hour)"
                      type="number"
                      step="0.01"
                      value={payrollSettings?.minimumWage}
                      onChange={(e) => setPayrollSettings({ ...payrollSettings, minimumWage: e?.target?.value })}
                    />
                  </div>
                </div>
              )}

              {/* Bank Configurations */}
              {activeTab === 'banks' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Bank Integration Setup</h2>
                  <div className="space-y-4">
                    {bankConfigs?.map((bank) => (
                      <div key={bank?.id} className="border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-foreground">{bank?.name}</h3>
                            <p className="text-sm text-muted-foreground">Bank Code: {bank?.code}</p>
                          </div>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={bank?.active}
                              onChange={() => toggleBankActive(bank?.id)}
                              className="rounded"
                            />
                            <span className="text-sm">Active</span>
                          </label>
                        </div>
                        {bank?.active && (
                          <div className="space-y-3">
                            <Input
                              label="API Endpoint"
                              value={bank?.apiEndpoint}
                              onChange={(e) => {
                                setBankConfigs(bankConfigs?.map(b => 
                                  b?.id === bank?.id ? { ...b, apiEndpoint: e?.target?.value } : b
                                ));
                              }}
                              placeholder="https://api.example.com"
                            />
                            <Input
                              label="API Key"
                              type="password"
                              value={bank?.apiKey}
                              onChange={(e) => {
                                setBankConfigs(bankConfigs?.map(b => 
                                  b?.id === bank?.id ? { ...b, apiKey: e?.target?.value } : b
                                ));
                              }}
                              placeholder="Enter API key"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GL Interface */}
              {activeTab === 'gl' && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">GL Interface Control</h2>
                  <div className="space-y-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={glInterface?.enabled}
                        onChange={(e) => setGlInterface({ ...glInterface, enabled: e?.target?.checked })}
                        className="rounded"
                      />
                      <span className="text-sm font-medium">Enable GL Interface</span>
                    </label>
                    {glInterface?.enabled && (
                      <>
                        <Select
                          label="ERP System"
                          value={glInterface?.erpSystem}
                          onChange={(e) => setGlInterface({ ...glInterface, erpSystem: e?.target?.value })}
                        >
                          <option value="">Select ERP System</option>
                          <option value="sap">SAP</option>
                          <option value="oracle">Oracle</option>
                          <option value="myob">MYOB</option>
                          <option value="xero">Xero</option>
                          <option value="quickbooks">QuickBooks</option>
                        </Select>
                        <div className="space-y-3">
                          <h3 className="font-medium text-foreground">Account Mapping</h3>
                          <Input
                            label="Salaries Account"
                            value={glInterface?.accountMapping?.salariesAccount}
                            onChange={(e) => setGlInterface({
                              ...glInterface,
                              accountMapping: { ...glInterface?.accountMapping, salariesAccount: e?.target?.value }
                            })}
                            placeholder="e.g., 5100"
                          />
                          <Input
                            label="Tax Account"
                            value={glInterface?.accountMapping?.taxAccount}
                            onChange={(e) => setGlInterface({
                              ...glInterface,
                              accountMapping: { ...glInterface?.accountMapping, taxAccount: e?.target?.value }
                            })}
                            placeholder="e.g., 2200"
                          />
                          <Input
                            label="Superannuation Account"
                            value={glInterface?.accountMapping?.superAccount}
                            onChange={(e) => setGlInterface({
                              ...glInterface,
                              accountMapping: { ...glInterface?.accountMapping, superAccount: e?.target?.value }
                            })}
                            placeholder="e.g., 2300"
                          />
                          <Input
                            label="Deductions Account"
                            value={glInterface?.accountMapping?.deductionsAccount}
                            onChange={(e) => setGlInterface({
                              ...glInterface,
                              accountMapping: { ...glInterface?.accountMapping, deductionsAccount: e?.target?.value }
                            })}
                            placeholder="e.g., 2400"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}